/**
 * Usage statistics display component.
 *
 * Shows user token usage and API cost in a compact format
 * suitable for the sidebar.
 */
import { useUserProfile } from '@/hooks/useUserProfile';
import { Progress } from '@/components/ui/Progress';
import { cn, formatCompactNumber } from '@/lib/utils';
import { Coins, Zap, Brain, DollarSign } from 'lucide-react';

function formatCost(cost: number): string {
  if (cost < 0.01) {
    return '<$0.01';
  }
  return `$${cost.toFixed(2)}`;
}

export function UsageStats() {
  const { data: profile, isLoading } = useUserProfile();

  if (isLoading || !profile) {
    return null;
  }

  const hasSpendingLimit = profile.spending_limit_usd !== null;
  const spendingPercent = hasSpendingLimit
    ? Math.min(100, (profile.estimated_cost_usd / profile.spending_limit_usd!) * 100)
    : 0;

  const totalTokens =
    profile.total_input_tokens +
    profile.total_output_tokens +
    profile.total_thinking_tokens;

  return (
    <div className="px-2 py-2 space-y-2">
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
        <Coins className="h-3 w-3" />
        <span>API Usage</span>
      </div>

      {/* Cost display */}
      <div className="flex items-center justify-between text-xs">
        <div className="flex items-center gap-1 text-muted-foreground">
          <DollarSign className="h-3 w-3" />
          <span>Cost</span>
        </div>
        <span
          className={cn(
            'font-medium',
            profile.is_over_limit ? 'text-destructive' : 'text-foreground'
          )}
        >
          {formatCost(profile.estimated_cost_usd)}
          {hasSpendingLimit && (
            <span className="text-muted-foreground">
              {' '}
              / ${profile.spending_limit_usd?.toFixed(0)}
            </span>
          )}
        </span>
      </div>

      {/* Spending progress bar (only if limit exists) */}
      {hasSpendingLimit && (
        <Progress
          value={spendingPercent}
          className="h-1.5"
          indicatorClassName={cn(
            spendingPercent > 90
              ? 'bg-destructive'
              : spendingPercent > 70
              ? 'bg-yellow-500'
              : 'bg-primary'
          )}
        />
      )}

      {/* Token breakdown */}
      <div className="space-y-1 pt-1">
        <div className="flex items-center justify-between text-xs">
          <div className="flex items-center gap-1 text-muted-foreground">
            <Zap className="h-3 w-3" />
            <span>Tokens</span>
          </div>
          <span className="font-medium text-foreground">
            {formatCompactNumber(totalTokens)}
          </span>
        </div>

        {/* Detailed breakdown on hover - using a simple tooltip-like expand */}
        <div className="grid grid-cols-3 gap-1 text-[10px] text-muted-foreground">
          <div className="flex flex-col items-center">
            <span className="font-medium text-foreground">
              {formatCompactNumber(profile.total_input_tokens)}
            </span>
            <span>in</span>
          </div>
          <div className="flex flex-col items-center">
            <span className="font-medium text-foreground">
              {formatCompactNumber(profile.total_output_tokens)}
            </span>
            <span>out</span>
          </div>
          <div className="flex flex-col items-center">
            <span className="font-medium text-foreground">
              {formatCompactNumber(profile.total_thinking_tokens)}
            </span>
            <span>
              <Brain className="h-2.5 w-2.5 inline" />
            </span>
          </div>
        </div>
      </div>

      {/* Request count */}
      <div className="flex items-center justify-between text-xs pt-1 border-t border-border/50">
        <span className="text-muted-foreground">Requests</span>
        <span className="font-medium text-foreground">{profile.total_requests}</span>
      </div>
    </div>
  );
}
